import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const stats = [
  { value: 500, suffix: "+", key: "clients", icon: "ri-user-smile-line" },
  { value: 10, suffix: "+", key: "years", icon: "ri-medal-line" },
  { value: 100, suffix: "%", key: "satisfaction", icon: "ri-thumb-up-fill" },
  { value: 247, suffix: "+", key: "reviews", icon: "ri-star-fill" },
];

const useCounter = (target: number, active: boolean) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const duration = 1800;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, active]);
  return count;
};

const StatItem = ({
  value,
  suffix,
  labelKey,
  icon,
  active,
}: {
  value: number;
  suffix: string;
  labelKey: string;
  icon: string;
  active: boolean;
}) => {
  const { t } = useTranslation();
  const count = useCounter(value, active);
  return (
    <div className="text-center px-4">
      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-[#D4AF37]/20">
        <i className={`${icon} text-xl text-[#D4AF37]`} />
      </div>
      <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-1">
        {count}
        {suffix}
      </div>
      <div className="text-white/70 text-sm font-medium uppercase tracking-wide">
        {t(`stat_${labelKey}`)}
      </div>
    </div>
  );
};

const StatsBar = () => {
  const [active, setActive] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setActive(true);
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-[#0F2A44] py-16 border-t border-white/10">
      <div className="max-w-5xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-x-0 md:divide-x divide-white/10">
          {stats.map((s, i) => (
            <StatItem
              key={s.key}
              value={s.value}
              suffix={s.suffix}
              labelKey={s.key}
              icon={s.icon}
              active={active}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsBar;
